<template>
  <article class="news-card">
    <a :href="href" class="news-card-link" @click.prevent="abrir">
      <div class="news-card-media">
        <img v-img-fallback v-if="getImage && !imageError" :src="getImage" :alt="newTitle" loading="lazy" @error="imageError = true" />
        <div v-else class="news-card-placeholder">
          <span class="material-symbols-outlined">article</span>
        </div>

        <span v-if="caritas" class="news-card-tag">Cáritas</span>

        <time v-if="fecha" class="news-card-date" :datetime="fechaISO">
          <span class="news-card-day">{{ fecha.getDate() }}</span>
          <span class="news-card-month">{{ months[fecha.getMonth()] }}<template v-if="showYear"> {{ fecha.getFullYear() }}</template></span>
        </time>
      </div>

      <div class="news-card-body">
        <h3 class="news-card-title">{{ newTitle }}</h3>
        <p v-if="resumenNoticia" class="news-card-summary">{{ resumenNoticia }}</p>
        <span class="news-card-more">
          Leer más <span class="material-symbols-outlined">arrow_forward</span>
        </span>
      </div>
    </a>
  </article>
</template>

<script>
import { track } from "@/directives/analitica.js";

export default {
  name: "cardNews",
  props: {
    imageSrc: String,
    newTitle: String,
    resumenNoticia: String,
    fechaPublicacion: [String, Number, Date],
    idnoticia: [String, Number],
    caritas: Boolean,
  },
  data() {
    return {
      base: this.$media,
      imageError: false,
      months: ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DIC"],
    };
  },
  computed: {
    getImage() {
      if (!this.imageSrc) return null;
      return this.imageSrc.includes("http") ? this.imageSrc : this.base + "/img/minGallery/" + this.imageSrc;
    },
    // Acepta "YYYY-MM-DD", "YYYY-MM-DD HH:MM:SS" o timestamps (new Date() falla con el espacio en Safari)
    fecha() {
      const f = this.fechaPublicacion;
      if (!f) return null;
      if (f instanceof Date || typeof f === "number") return new Date(f);
      const m = String(f).match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/);
      if (m) return new Date(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0));
      const d = new Date(f);
      return isNaN(d) ? null : d;
    },
    fechaISO() {
      const d = this.fecha;
      if (!d) return "";
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    },
    showYear() {
      return this.fecha && this.fecha.getFullYear() !== new Date().getFullYear();
    },
    href() {
      return this.$router.resolve({ name: "noticia", query: { id: this.idnoticia } }).href;
    },
  },
  watch: {
    imageSrc() {
      this.imageError = false;
    },
  },
  methods: {
    abrir() {
      track("select_content", {
        content_type: this.caritas ? "noticia_caritas" : "noticia",
        item_id: String(this.idnoticia),
        item_name: this.newTitle,
        origen: this.$route.name,
      });
      this.$emit("getIdNews", { id: this.idnoticia });
    },
  },
};
</script>

<style scoped>
.news-card {
  height: 100%;
}

.news-card-link {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  border: 1px solid var(--sd-border);
  border-radius: var(--sd-radius);
  background: var(--sd-surface);
  box-shadow: var(--sd-shadow);
  color: var(--sd-text);
  text-decoration: none;
  text-align: left;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.news-card-link:hover {
  transform: translateY(-4px);
  box-shadow: var(--sd-shadow-lg);
  color: var(--sd-text);
  text-decoration: none;
}

.news-card-link:focus-visible {
  outline: 3px solid var(--sd-primary);
  outline-offset: 3px;
}

/* Imagen */
.news-card-media {
  position: relative;
  aspect-ratio: 16 / 10;
  overflow: hidden;
  background: var(--sd-surface-2);
}

.news-card-media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s ease;
}

.news-card-link:hover .news-card-media img {
  transform: scale(1.06);
}

.news-card-placeholder {
  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, var(--sd-primary-soft), var(--sd-gold-soft));
}

.news-card-placeholder .material-symbols-outlined {
  font-size: 48px;
  color: var(--sd-primary);
  opacity: 0.6;
}

.news-card-tag {
  position: absolute;
  top: 14px;
  left: 14px;
  padding: 4px 10px;
  border-radius: 999px;
  background: #b91c1c;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #fff;
}

.news-card-date {
  position: absolute;
  top: 14px;
  right: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 54px;
  padding: 6px 8px;
  border-radius: 12px;
  background: var(--sd-surface);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
  line-height: 1.1;
}

.news-card-day {
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--sd-text);
}

.news-card-month {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: var(--sd-primary);
}

/* Texto */
.news-card-body {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 10px;
  padding: 20px 22px 22px;
}

.news-card-title {
  display: -webkit-box;
  margin: 0;
  overflow: hidden;
  font-size: 1.12rem;
  font-weight: 700;
  line-height: 1.35;
  color: var(--sd-text);
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  transition: color 0.2s;
}

.news-card-link:hover .news-card-title {
  color: var(--sd-primary);
}

.news-card-summary {
  display: -webkit-box;
  margin: 0;
  overflow: hidden;
  font-size: 0.93rem;
  line-height: 1.55;
  color: var(--sd-muted);
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

.news-card-more {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: auto;
  padding-top: 8px;
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--sd-primary);
}

.news-card-more .material-symbols-outlined {
  font-size: 18px;
  color: inherit;
  transition: transform 0.2s;
}

.news-card-link:hover .news-card-more .material-symbols-outlined {
  transform: translateX(4px);
}

@media (prefers-reduced-motion: reduce) {
  .news-card-link,
  .news-card-media img {
    transition: none;
  }
}
</style>
